import { useCallback, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { Band, Event } from '../types/database';

export type EventWithBand = Omit<Event, 'band'> & {
  band: { id: string; name: string; slug: string; logo_url: string | null; location_name: string | null };
};

/** Unified search: searches bands by name, location, or country with partial matching */
export function useUnifiedSearch() {
  const [bands, setBands] = useState<Band[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const search = useCallback(async (query: string) => {
    if (!query.trim()) {
      setBands([]);
      setSearched(false);
      return;
    }

    setLoading(true);
    setSearched(true);

    const q = query.trim();

    // Search across band name, location, and country with partial matching
    const { data, error } = await supabase
      .from('bands')
      .select('*')
      .eq('is_public', true)
      .or(`name.ilike.%${q}%,location_name.ilike.%${q}%,location_country.ilike.%${q}%`)
      .order('name')
      .limit(50);

    if (error) {
      console.error('Search error:', error.message);
    }

    setBands(data ?? []);
    setLoading(false);
  }, []);

  return { bands, loading, searched, search };
}

/** Search bands by GPS proximity */
export function useNearbySearch() {
  const [bands, setBands] = useState<(Band & { distance_km: number })[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [locationName, setLocationName] = useState<string | null>(null);

  const searchNearby = useCallback(async (lat: number, lng: number, radiusKm = 100) => {
    setLoading(true);
    setSearched(true);
    setLocationName(null);

    const { data, error } = await supabase.rpc('search_bands_nearby', {
      user_lat: lat,
      user_lng: lng,
      radius_km: radiusKm,
    });

    if (error) {
      console.error('Nearby search error:', error.message);
    }

    setBands((data as (Band & { distance_km: number })[]) ?? []);
    setLoading(false);

    // Reverse geocode to get a friendly location name
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&zoom=10`,
        { headers: { 'Accept-Language': 'en' } }
      );
      if (res.ok) {
        const geo = await res.json();
        const city = geo.address?.city || geo.address?.town || geo.address?.village || geo.address?.county;
        const country = geo.address?.country;
        setLocationName(city ? `${city}, ${country}` : country || null);
      }
    } catch {
      // Reverse geocode is best-effort
    }
  }, []);

  return { bands, loading, searched, locationName, searchNearby };
}

/** Legacy location search - kept for backwards compat */
export function useLocationSearch() {
  const [results, setResults] = useState<Band[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  async function searchByLocation(query: string) {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    setLoading(true);
    setSearched(true);

    const { data } = await supabase
      .from('bands')
      .select('*')
      .eq('is_public', true)
      .or(`location_name.ilike.%${query}%,location_country.ilike.%${query}%`)
      .order('name');

    setResults(data ?? []);
    setLoading(false);
  }

  return { results, loading, searched, searchByLocation };
}

export function useBandSearch() {
  const [results, setResults] = useState<Band[]>([]);
  const [loading, setLoading] = useState(false);

  async function search(query: string) {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    setLoading(true);

    const { data } = await supabase
      .from('bands')
      .select('*')
      .eq('is_public', true)
      .or(`name.ilike.%${query}%,genres.cs.{${query}},location_name.ilike.%${query}%`)
      .order('name')
      .limit(20);

    setResults(data ?? []);
    setLoading(false);
  }

  return { results, loading, search };
}

/** Fetch public events for a set of bands (or all public events matching a location) */
export function useSearchEvents() {
  const [events, setEvents] = useState<EventWithBand[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchByBandIds = useCallback(async (bandIds: string[]) => {
    if (bandIds.length === 0) {
      setEvents([]);
      return;
    }

    setLoading(true);

    const { data } = await supabase
      .from('events')
      .select('*, band:bands(id, name, slug, logo_url, location_name)')
      .in('band_id', bandIds)
      .eq('visibility', 'public')
      .gte('start_time', new Date().toISOString())
      .order('start_time', { ascending: true })
      .limit(200);

    setEvents((data as unknown as EventWithBand[]) ?? []);
    setLoading(false);
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);

    const { data } = await supabase
      .from('events')
      .select('*, band:bands(id, name, slug, logo_url, location_name)')
      .eq('visibility', 'public')
      .gte('start_time', new Date().toISOString())
      .order('start_time', { ascending: true })
      .limit(200);

    setEvents((data as unknown as EventWithBand[]) ?? []);
    setLoading(false);
  }, []);

  return { events, loading, fetchByBandIds, fetchAll };
}
